import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Table,
  Badge,
  InputGroup,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUserPlus,
  faSearch,
  faEdit,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import {
  ChevronRightIcon,
  Edit,
  MoreVertical,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { FaEdit, FaTrash, FaUser, FaUserPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { AllTeams } from "../../../interfaces/identity/calendar/Events";
import { useAuth } from "../../../firebase/AuthProvider";
import { getAllTeam } from "../../../services/calendarService/EventService";
import { DeleteTeamAsync } from "../../../services/teamandregion/TeamandRegionService";
import { ROLE_PRIORITY, ROLES, TEAM_LIMIT } from "../../../Data/SD";
import {
  TeamMemberDto,
  AddUserState,
} from "../../../interfaces/team/TeamandRegion.modal";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import Loader from "../loader/Loader";
import AddTeamModal from "./modals/AddTeamModal";
import AddUserModal from "../userComponents/UserManagement/AddUser";
import EditUserModal from "../userComponents/UserManagement/EditUserModal";
import { updateUserStatus, getUserById } from "../../../services/userServices/TeamService";
import { forgotPassword } from "../../../services/userServices/UserOrganizationService";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import Team from "../../../assets/images/Team.svg";
import LockRounded from "../../../assets/images/lock-rounded.svg";
import "./TeamsTab.css";

const NOT_ALLOWED_ROLES : string[] = [ROLES?.DEVELOPER , ROLES?.SCRUM_MASTER];

const TeamsTab = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Modal State
  const [addTeameModal, setAddTeamModal] = useState<boolean>(false);
  const [addUserModal, setAddUserModal] = useState<boolean>(false);
  const [editUserModal, setEditUserModal] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<AllTeams | null>(null);
  const [editTeamData, setEditTeamData] = useState<AllTeams | null>(null);
  const [teamForAddUser, setTeamForAddUser] = useState<AllTeams | null>(null);

  // Data state
  const [teams, setTeams] = useState<AllTeams[]>([]);

  // Other states
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [IsLoading, setIsLoading] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState<{ userId: string; firstName: string; lastName: string; email: string } | null>(null);
  const [sendingPasswordReset, setSendingPasswordReset] = useState<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (!target.closest("[data-team-menu]")) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    GetAllTeams();
  }, [currentUser]);

  const handleCloseAddTeamModal = () => {
    setAddTeamModal(false);
    setEditTeamData(null);
  };
  const handleShowAddTeamModal = () => {
    setEditTeamData(null);
    setAddTeamModal(true);
  };
  const handleShowEditTeamModal = (team: AllTeams) => {
    setEditTeamData(team);
    setAddTeamModal(true);
    setOpenMenuId(null);
  };

  const handleCloseAddUserModal = () => {
    setAddUserModal(false);
    setTeamForAddUser(null);
  };
  const handleShowAddUserModal = (team: AllTeams | null = null) => {
    setTeamForAddUser(team);
    setAddUserModal(true);
  };

  const handleCloseEditUserModal = () => {
    setEditUserModal(false);
    setSelectedUserId(null);
  };
  const handleShowEditUserModal = (userId: string) => {
    setSelectedUserId(userId);
    setEditUserModal(true);
  };

  const GetAllTeams = async () => {
    const token = await currentUser?.getIdToken();
    if (!token) return;
    setIsLoading(true);
    getAllTeam(token)
      .then((res) => {
        if (res.success) {
          setTeams(res.data);
          // Update selectedTeam if it's set, to reflect changes immediately
          if (selectedTeam) {
            const updatedTeam = res.data.find((team) => team.id === selectedTeam.id);
            if (updatedTeam) {
              setSelectedTeam(updatedTeam);
            } else {
              // If the selected team is no longer in the list (e.g., deleted), clear selection
              setSelectedTeam(null);
            }
          }
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getHighestRole = (member: TeamMemberDto): string | null => {
    if (!member.roleNames.length) return null;
    return member.roleNames.reduce((highest, role) =>
      ROLE_PRIORITY[role] > ROLE_PRIORITY[highest] ? role : highest
    );
  };

  const navigateToAddUser = () => {
    handleShowAddUserModal(null);
  };

  const navigateToEditUser = (userId: string) => {
    handleShowEditUserModal(userId);
  };

  const handleToggleStatus = async (userId: string, newStatus: boolean) => {
    const isConfirm = await showConfirmationDialog(
      `Are you sure you want to ${!newStatus ? "activate" : "deactivate"
      } this user?`,
      `${!newStatus
        ? ""
        : "User deactivation will revoke system access and remove this user from all task views, filters, and assignment options. Any participants assigned to this user must be reassigned prior to deactivation."
      }`
    );
    if (!isConfirm) return;
    const token = await currentUser?.getIdToken();

    if (!token) return;
    setIsLoading(true);
    const ResSuccess = await updateUserStatus(userId, newStatus, token);

    if (ResSuccess) {
      setSelectedTeam((prevTeam) => {
        if (!prevTeam) return prevTeam;

        const updatedMembers = prevTeam.members.map((member) =>
          member.userId === userId
            ? { ...member, deactivated: newStatus }
            : member
        );

        return { ...prevTeam, members: updatedMembers };
      });
    } else {
      toast.error("Failed to update status. Please try again.");
    }

    setIsLoading(false);
  };

  const handleUserAddSuccess = () => {
    GetAllTeams();
  };

  const handlePasswordResetClick = async (member: TeamMemberDto) => {
    const token = await currentUser?.getIdToken();
    if (!token) {
      toast.error("Unable to authenticate. Please try again.");
      return;
    }

    setIsLoading(true);
    try {
      const userResponse = await getUserById(member.userId, token);
      if (userResponse && userResponse.data && userResponse.data.email) {
        setPasswordResetUser({
          userId: member.userId,
          firstName: member.firstName,
          lastName: member.lastName,
          email: userResponse.data.email,
        });
        setShowPasswordResetModal(true);
      } else {
        toast.error("Unable to retrieve user email. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching user email:", error);
      toast.error("An error occurred while retrieving user information.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!passwordResetUser || !passwordResetUser.email) {
      toast.error("Unable to send password reset email. User email not found.");
      return;
    }

    setSendingPasswordReset(true);
    try {
      const success = await forgotPassword(passwordResetUser.email);
      if (success) {
        toast.success("Password reset email sent.");
      } else {
        toast.error("Failed to send password reset email. Please try again.");
      }
      setShowPasswordResetModal(false);
      setPasswordResetUser(null);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      toast.error("An error occurred while sending the password reset email. Please try again.");
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleDeleteTeam = async (team: AllTeams) => {
    const isConfirm = await showConfirmationDialog(
      "Are you sure you want to delete team?",
      `This action cannot be undone!`,
      "Yes, delete it!",
      "Cancel"
    );
    if (!isConfirm) return;

    const token = await currentUser?.getIdToken();
    if (!token) return;

    setIsLoading(true);
    try {
      const res = await DeleteTeamAsync(token, team.id);
      if (res.success) {
        toast.success("Team deleted successfully");
        // If the deleted team was selected, clear selection immediately
        if (selectedTeam?.id === team.id) {
          setSelectedTeam(null);
        }
        // Refresh the teams list
        GetAllTeams();
      } else {
        toast.error(res.message || "Failed to delete team");
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred while deleting the team");
    } finally {
      setIsLoading(false);
    }
  };

  if (selectedTeam) {
    // 1) Filter the selected team members based on the activated or deactivated
    const filteredSelectedTeam = selectedTeam?.members?.filter((raw) => (!raw?.deactivated));
    // 2) Get the count of only the activated members
    const roleCounts = filteredSelectedTeam?.reduce<Record<string, number>>(
      (acc, member) => {
        const highestRole = getHighestRole(member);
        if (highestRole) {
          acc[highestRole] = (acc[highestRole] || 0) + 1;
        }
        return acc;
      },
      {}
    );

    const activeMembersCount = selectedTeam.members.filter(
      (member) => !member.deactivated
    ).length;
    const totalMembersCount = selectedTeam.members.length;

    const filteredMembers = selectedTeam.members
      .filter((member) => {
        const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
        const search = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm || fullName.includes(search);

        if (roleFilter === "all") return matchesSearch;
        const highestRole = getHighestRole(member);
        return matchesSearch && highestRole === roleFilter;
      })
      .sort((a, b) => {
        const roleA = getHighestRole(a) || "";
        const roleB = getHighestRole(b) || "";
        return (ROLE_PRIORITY[roleB] || 0) - (ROLE_PRIORITY[roleA] || 0);
      });

    // Get all roles to display in cards, even if count is 0
    const allRoles = [
      ROLES.SUPERVISOR,
      ROLES.SC,
      ROLES.PROGRAM_MANAGER,
      ROLES.ASSESSOR,
      ROLES.SCRUM_MASTER,
      ROLES.OWNER,
    ];

    return (
      <div style={{ padding: "1.5rem 3rem", backgroundColor: "white", minHeight: "100vh" }}>
        {IsLoading && <Loader />}

        {/* Header Section */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1.5rem"
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
            <button
              onClick={() => setSelectedTeam(null)}
              className="team-header-back-btn"
              style={{
                fontSize: "1.25rem",
                color: "#374151",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
                marginTop: "0.25rem"
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                <path d="M8.71515 14.4625L3.38672 9.1341L8.71515 3.80566" stroke="#0A0A0A" stroke-width="1.52241" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14.0436 9.13477H3.38672" stroke="#0A0A0A" stroke-width="1.52241" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div>
              <h2 className="team-header-title">
                {selectedTeam?.teamName}
              </h2>
              <p className="team-header-subtitle" style={{
                fontSize: "0.875rem",
                color: "#6B7280",
                margin: 0,
                lineHeight: "1.2"
              }}>
                {selectedTeam?.regionName}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button
              // Pass selectedTeam to ensure Region/Teams fields are hidden in the modal
              onClick={() => handleShowAddUserModal(selectedTeam)}
              className="team-add-member-btn"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M11.8523 15.5558V14.0743C11.8523 13.2885 11.5401 12.5348 10.9845 11.9792C10.4288 11.4235 9.67517 11.1113 8.88934 11.1113H4.4449C3.65907 11.1113 2.90543 11.4235 2.34977 11.9792C1.7941 12.5348 1.48193 13.2885 1.48193 14.0743V15.5558"
                  stroke="white"
                  strokeWidth="1.48148"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66706 8.14761C8.30346 8.14761 9.63003 6.82104 9.63003 5.18464C9.63003 3.54824 8.30346 2.22168 6.66706 2.22168C5.03067 2.22168 3.7041 3.54824 3.7041 5.18464C3.7041 6.82104 5.03067 8.14761 6.66706 8.14761Z"
                  stroke="white"
                  strokeWidth="1.48148"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.2964 15.5552V14.0737C16.296 13.4172 16.0774 12.7795 15.6752 12.2606C15.273 11.7417 14.7099 11.3712 14.0742 11.207"
                  stroke="white"
                  strokeWidth="1.48148"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.8521 2.31836C12.4894 2.48155 13.0543 2.85221 13.4577 3.37192C13.8611 3.89163 14.0801 4.53083 14.0801 5.18873C14.0801 5.84663 13.8611 6.48583 13.4577 7.00554C13.0543 7.52525 12.4894 7.89591 11.8521 8.0591"
                  stroke="white"
                  strokeWidth="1.48148"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={{
                color: "#FFF",  
                textAlign: "center",
                fontFamily: "Poppins",
                fontSize: "1rem",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "1.38888rem"
              }}> Add Member</span>
            </button>
            <div className="team-member-count">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M9 9C11.0711 9 12.75 7.32107 12.75 5.25C12.75 3.17893 11.0711 1.5 9 1.5C6.92893 1.5 5.25 3.17893 5.25 5.25C5.25 7.32107 6.92893 9 9 9Z"
                  stroke="#374151"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.4425 16.5C15.4425 13.5147 12.4853 11.25 9 11.25C5.51472 11.25 2.5575 13.5147 2.5575 16.5"
                  stroke="#374151"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{totalMembersCount}</span>
            </div>
            {/* <button
              className="team-ellipsis-btn"
              style={{
                background: "none",
                border: "none",
                color: "#374151",
                cursor: "pointer",
                padding: "0.25rem",
                display: "flex",
                alignItems: "center"
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 17 17"
                fill="none"
              >
                <path
                  d="M8.1811 8.86221C8.55781 8.86221 8.86319 8.55683 8.86319 8.18013C8.86319 7.80342 8.55781 7.49805 8.1811 7.49805C7.8044 7.49805 7.49902 7.80342 7.49902 8.18013C7.49902 8.55683 7.8044 8.86221 8.1811 8.86221Z"
                  stroke="#374151"
                  strokeWidth="1.36344"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.1811 4.09072C8.55781 4.09072 8.86319 3.78535 8.86319 3.40864C8.86319 3.03194 8.55781 2.72656 8.1811 2.72656C7.8044 2.72656 7.49902 3.03194 7.49902 3.40864C7.49902 3.78535 7.8044 4.09072 8.1811 4.09072Z"
                  stroke="#374151"
                  strokeWidth="1.36344"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.1811 13.6347C8.55781 13.6347 8.86319 13.3293 8.86319 12.9526C8.86319 12.5759 8.55781 12.2705 8.1811 12.2705C7.8044 12.2705 7.49902 12.5759 7.49902 12.9526C7.49902 13.3293 7.8044 13.6347 8.1811 13.6347Z"
                  stroke="#374151"
                  strokeWidth="1.36344"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button> */}
          </div>
        </div>

        {/* Horizontal Line */}
        <div style={{
          height: "1px",
          backgroundColor: "#E5E7EB",
          marginBottom: "1.5rem",
          marginLeft: "-1.5rem",
          marginRight: "-1.5rem",
          width: "calc(100% + 3rem)"
        }}></div>

        {/* Summary Cards Section */}
        <div style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          flexWrap: "wrap"
        }}>
          {allRoles.map((role) => {
            const count = roleCounts[role] || 0;
            return (
              <div
                key={role}
                className="team-role-card"
                style={{
                  background: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  padding: "0.5rem 1rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "80px",
                  flex: "1",
                  minWidth: "120px"
                }}
              >
                <div className="team-role-card-number" style={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#111827",
                  marginBottom: "0.25rem"
                }}>
                  {count}
                </div>
                <div className="team-role-card-label" style={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  textAlign: "center"
                }}>
                  {role}
                </div>
              </div>
            );
          })}
        </div>

        {/* Table Section */}
        <div className="team-table-container">
          {/* Search and Filter Bar */}
          <div style={{
            display: "flex",
            gap: "1rem",
            margin: "1rem 1.5rem",
            flexWrap: "wrap"
          }}>
            <div style={{ position: "relative", width: "300px" }}>
              <FontAwesomeIcon
                icon={faSearch}
                className="team-search-icon"
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9CA3AF",
                  pointerEvents: "none"
                }}
              />
              <input
                type="text"
                placeholder="Search Document"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="team-search-input"
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  padding: "0.5rem 0.75rem 0.5rem 2.5rem",
                  fontSize: "0.875rem",
                  width: "100%",
                  outline: "none"
                }}
              />
            </div>
            <div style={{ minWidth: "150px" }}>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="team-role-select"
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  padding: "0.5rem 2rem 0.5rem 0.75rem",
                  fontSize: "0.875rem",
                  background: "white",
                  cursor: "pointer",
                  outline: "none",
                  width: "100%",
                  appearance: "none",
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "12px"
                }}
              >
                <option value="all">All Roles</option>
                {Object.entries(ROLES).filter(([key, value]) => (value !== ROLES?.ADMIN && value !== ROLES?.SCRUM_MASTER && value !== ROLES?.DEVELOPER)).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Table */}
          <table className="team-table">
            <thead className="bg-[#FAFAFA]">
              <tr>
                <th className="team-table-header">NAME</th>
                <th className="team-table-header">REGION</th>
                <th className="team-table-header">ROLE</th>
                <th className="team-table-header">STATUS</th>
                <th className="team-table-header">ACTION</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={5} style={{
                  padding: 0,
                  borderBottom: "1px solid #F3F4F6"
                }}>
                  <div style={{
                    height: "1px",
                    backgroundColor: "#F3F4F6",
                    width: "100%"
                  }}></div>
                </td>
              </tr>
              {filteredMembers.map((member, index) => {
                const highestRole = getHighestRole(member);
                const isLastRow = index === filteredMembers.length - 1;
                const borderBottomStyle = isLastRow ? "none" : "1px solid #F3F4F6";
                return (
                  <tr key={member.userId}>
                    <td style={{
                      padding: "1rem",
                      borderBottom: borderBottomStyle,
                      fontSize: "0.875rem",
                      color: "#6B7280"
                    }}>
                      <div className="team-name-cell">
                        {/* <svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M0 9.23885C0 4.13638 4.13637 0 9.23884 0H27.7165C32.819 0 36.9554 4.13637 36.9554 9.23884V27.7165C36.9554 32.819 32.819 36.9554 27.7165 36.9554H9.23885C4.13638 36.9554 0 32.819 0 27.7165V9.23885Z" fill="url(#paint0_linear_3290_5886)" />
                          <path d="M20.4992 11.7412H14.4362C14.0789 11.7412 13.7362 11.8832 13.4835 12.1358C13.2308 12.3885 13.0889 12.7312 13.0889 13.0885V23.8672C13.0889 24.2245 13.2308 24.5672 13.4835 24.8199C13.7362 25.0726 14.0789 25.2145 14.4362 25.2145H22.5202C22.8775 25.2145 23.2202 25.0726 23.4729 24.8199C23.7256 24.5672 23.8675 24.2245 23.8675 23.8672V15.1095L20.4992 11.7412Z" stroke="#8B5CF6" stroke-width="1.0105" stroke-linecap="round" stroke-linejoin="round" />
                          <path d="M19.8232 11.7412V14.4359C19.8232 14.7932 19.9652 15.1359 20.2179 15.3886C20.4705 15.6413 20.8132 15.7832 21.1706 15.7832H23.8652" stroke="#8B5CF6" stroke-width="1.0105" stroke-linecap="round" stroke-linejoin="round" />
                          <path d="M17.1286 16.457H15.7812" stroke="#8B5CF6" stroke-width="1.0105" stroke-linecap="round" stroke-linejoin="round" />
                          <path d="M21.1706 19.1514H15.7812" stroke="#8B5CF6" stroke-width="1.0105" stroke-linecap="round" stroke-linejoin="round" />
                          <path d="M21.1706 21.8457H15.7812" stroke="#8B5CF6" stroke-width="1.0105" stroke-linecap="round" stroke-linejoin="round" />
                          <defs>
                            <linearGradient id="paint0_linear_3290_5886" x1="0" y1="0" x2="36.9554" y2="36.9554" gradientUnits="userSpaceOnUse">
                              <stop stop-color="#8B5CF6" stop-opacity="0.1" />
                              <stop offset="1" stop-color="#8B5CF6" stop-opacity="0.05" />
                            </linearGradient>
                          </defs>
                        </svg> */}
                        <AvatarImage userId={member.userId} size="xs"/>


                        <span style={{ fontWeight: 400 }} className="ms-2">
                          {member.firstName} {member.lastName}
                        </span>
                      </div>
                    </td>
                    <td style={{
                      padding: "1rem",
                      borderBottom: borderBottomStyle,
                      fontSize: "0.875rem",
                      color: "#6B7280"
                    }}>
                      {selectedTeam.regionName}
                    </td>
                    <td style={{
                      padding: "1rem",
                      borderBottom: borderBottomStyle,
                      fontSize: "0.875rem",
                      color: "#6B7280"
                    }}>
                      {highestRole || member.roleNames.join(", ")}
                    </td>
                    <td style={{
                      padding: "1rem",
                      borderBottom: borderBottomStyle,
                      fontSize: "0.875rem",
                      color: "#6B7280"
                    }}>
                      {currentUser?.uid !== member.userId ? (
                        <label className="team-toggle-switch" style={{
                          position: "relative",
                          display: "inline-block",
                          width: "44px",
                          height: "24px",
                          cursor: "pointer"
                        }}>
                          <input
                            type="checkbox"
                            checked={!member.deactivated}
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleStatus(member.userId, !member.deactivated);
                            }}
                            style={{
                              opacity: 0,
                              width: 0,
                              height: 0
                            }}
                          />
                          <span className="team-toggle-slider" style={{
                            position: "absolute",
                            cursor: "pointer",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: !member.deactivated ? "#7F56D9" : "#D1D5DB",
                            transition: "0.3s",
                            borderRadius: "24px"
                          }}>
                            <span style={{
                              position: "absolute",
                              height: "18px",
                              width: "18px",
                              left: !member.deactivated ? "23px" : "3px",
                              bottom: "3px",
                              backgroundColor: "white",
                              transition: "0.3s",
                              borderRadius: "50%"
                            }}></span>
                          </span>
                        </label>
                      ) : (
                        "--"
                      )}
                    </td>
                    <td style={{
                      padding: "1rem",
                      borderBottom: borderBottomStyle,
                      fontSize: "0.875rem",
                      color: "#111827"
                    }}>
                      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                        <div onClick={() => navigateToEditUser(member.userId)} style={{ cursor: "pointer" }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M10 0C10.2549 0.000282707 10.5 0.0978791 10.6854 0.272848C10.8707 0.447818 10.9822 0.686953 10.9972 0.941395C11.0121 1.19584 10.9293 1.44638 10.7657 1.64183C10.6021 1.83729 10.3701 1.9629 10.117 1.993L10 2H2V16H16V8C16.0003 7.74512 16.0979 7.49997 16.2728 7.31463C16.4478 7.1293 16.687 7.01776 16.9414 7.00283C17.1958 6.98789 17.4464 7.07067 17.6418 7.23426C17.8373 7.39785 17.9629 7.6299 17.993 7.883L18 8V16C18.0002 16.5046 17.8096 16.9906 17.4665 17.3605C17.1234 17.7305 16.6532 17.9572 16.15 17.995L16 18H2C1.49542 18.0002 1.00943 17.8096 0.639452 17.4665C0.269471 17.1234 0.0428434 16.6532 0.00500021 16.15L1.00268e-07 16V2C-0.000159579 1.49542 0.190406 1.00943 0.533497 0.639452C0.876588 0.269471 1.34684 0.0428433 1.85 0.00500011L2 0H10ZM16.243 0.343C16.423 0.163652 16.6644 0.0595265 16.9184 0.0517719C17.1723 0.0440173 17.4197 0.133215 17.6103 0.301249C17.8008 0.469282 17.9203 0.703552 17.9444 0.956475C17.9685 1.2094 17.8954 1.46201 17.74 1.663L17.657 1.758L7.757 11.657C7.57704 11.8363 7.33557 11.9405 7.08162 11.9482C6.82767 11.956 6.58029 11.8668 6.38972 11.6988C6.19916 11.5307 6.07969 11.2964 6.0556 11.0435C6.03151 10.7906 6.10459 10.538 6.26 10.337L6.343 10.243L16.243 0.343Z" fill="#7E57C2" />
                          </svg>
                        </div>
                        <img
                          src={LockRounded}
                          alt="Send Password Reset Email"
                          onClick={() => handlePasswordResetClick(member)}
                          style={{
                            cursor: "pointer",
                            width: "1.25rem",
                            height: "1.25rem"
                          }}
                          title="Send Password Reset Email"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <AddUserModal
          isOpen={addUserModal}
          onClose={handleCloseAddUserModal}
          onSuccess={handleUserAddSuccess}
          initialTeam={teamForAddUser}
        />
        <EditUserModal
          isOpen={editUserModal}
          onClose={handleCloseEditUserModal}
          userId={selectedUserId}
          onSuccess={handleUserAddSuccess}
        />

        {showPasswordResetModal && passwordResetUser && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[100]">
            <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full">
              <h2 className="text-lg font-semibold mb-4">Send Password Reset Email</h2>
              <p className="mb-2 text-gray-700">
                Are you sure you want to send a password reset email to:
              </p>
              <p className="mb-4 font-medium text-gray-900">
                {passwordResetUser.firstName} {passwordResetUser.lastName} ({passwordResetUser.email})
              </p>
              <p className="mb-6 text-sm text-gray-600">
                This action will send a password reset email to the employee. Use this feature for support and recovery purposes only.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setPasswordResetUser(null);
                  }}
                  disabled={sendingPasswordReset}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendPasswordReset}
                  disabled={sendingPasswordReset}
                  style={{ backgroundColor: "var(--primary-color)" }}
                  className="px-4 py-2 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingPasswordReset ? "Sending..." : "Send Reset Email"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {IsLoading && <Loader />}
      <div>
        <div className=" flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="flex align-items-center flex-wrap gap-1" >
            <h2
              className="mb-0"
              style={{
                color: "#212121",
                fontFamily: "Poppins",
                fontSize: "1.2rem",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "1.25rem",
              }}
            >
              All Teams{" "}
              <span style={{ color: "#747474", fontSize: "1.1rem"}}>
                ({teams.length})
              </span>
            </h2>

          </div>

          <div className="flex gap-3 flex-wrap">
            <button
              className="bg-[#7E57C2] flex  justify-between gap-[0.69444rem] py-[0.5rem] rounded-[0.625rem]  px-[1rem]"
              onClick={navigateToAddUser}
              style={{
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Poppins",
                fontSize: "1rem",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "1.38888rem"
              }}
            >
              {/* <FontAwesomeIcon
                icon={faUserPlus}
                className="w-[1.11rem]  h-[1.1rem]"
              /> */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1.11rem"
                height="1.11rem"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M11.8523 15.5558V14.0743C11.8523 13.2885 11.5401 12.5348 10.9845 11.9792C10.4288 11.4235 9.67517 11.1113 8.88934 11.1113H4.4449C3.65907 11.1113 2.90543 11.4235 2.34977 11.9792C1.7941 12.5348 1.48193 13.2885 1.48193 14.0743V15.5558"
                  stroke="white"
                  stroke-width="1.48148"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6.66706 8.14761C8.30346 8.14761 9.63003 6.82104 9.63003 5.18464C9.63003 3.54824 8.30346 2.22168 6.66706 2.22168C5.03067 2.22168 3.7041 3.54824 3.7041 5.18464C3.7041 6.82104 5.03067 8.14761 6.66706 8.14761Z"
                  stroke="white"
                  stroke-width="1.48148"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M16.2964 15.5552V14.0737C16.296 13.4172 16.0774 12.7795 15.6752 12.2606C15.273 11.7417 14.7099 11.3712 14.0742 11.207"
                  stroke="white"
                  stroke-width="1.48148"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M11.8521 2.31836C12.4894 2.48155 13.0543 2.85221 13.4577 3.37192C13.8611 3.89163 14.0801 4.53083 14.0801 5.18873C14.0801 5.84663 13.8611 6.48583 13.4577 7.00554C13.0543 7.52525 12.4894 7.89591 11.8521 8.0591"
                  stroke="white"
                  stroke-width="1.48148"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Add Member
            </button>
            <button
              className="bg-[#7E57C2] flex justify-between gap-[0.69444rem] py-[0.5rem] rounded-[0.625rem]  px-[1rem]"
              onClick={handleShowAddTeamModal}
              style={{
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Poppins",
                fontSize: "1rem",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "1.38888rem"
              }}
            >
              <img src={Team} alt="Team" className="w-[1.06rem]  h-[1.1rem]" />
              Add Team
            </button>
          </div>
        </div>

        <div className="row ">
          {teams.map((team) => {
            const roleCounts = team.members.reduce<Record<string, number>>(
              (acc, member) => {
                const highestRole = getHighestRole(member);
                if (highestRole) {
                  acc[highestRole] = (acc[highestRole] || 0) + 1;
                }
                return acc;
              },
              {}
            );

            const programManager = team.members.find((m) =>
              m.roleNames.includes(ROLES.PROGRAM_MANAGER)
            );

            return (
              <div
                key={team.id}
                className="col-sm-12 col-md-6 col-lg-6 col-xl-4 mb-4 d-flex"
              >
                <div
                  className="relative w-full   min-h-[21rem] cursor-pointer rounded-[0.639rem]   bg-white px-[1.5rem] pt-[1.5rem] pb-[1.5rem]  shadow-sm transition hover:shadow-md"
                  onClick={() => setSelectedTeam(team)}
                >
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-[0.76rem] flex-1 min-w-0">
                      <div className="flex h-[2.5rem] w-[2.5rem] sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-[0.639rem] bg-[#F3E8FF] text-[#7E57C2]">
                        {/* <Users size={16} className="sm:w-5 sm:h-5" /> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="1.27rem"
                          height="1.27rem"
                          viewBox="0 0 21 21"
                          fill="none"
                        >
                          <path
                            d="M13.6342 17.8951V16.1908C13.6342 15.2868 13.275 14.4198 12.6358 13.7806C11.9966 13.1413 11.1296 12.7822 10.2256 12.7822H5.11269C4.20868 12.7822 3.34169 13.1413 2.70245 13.7806C2.06322 14.4198 1.7041 15.2868 1.7041 16.1908V17.8951"
                            stroke="#7E57C2"
                            stroke-width="1.70429"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7.66884 9.37382C9.55135 9.37382 11.0774 7.84774 11.0774 5.96523C11.0774 4.08272 9.55135 2.55664 7.66884 2.55664C5.78633 2.55664 4.26025 4.08272 4.26025 5.96523C4.26025 7.84774 5.78633 9.37382 7.66884 9.37382Z"
                            stroke="#7E57C2"
                            stroke-width="1.70429"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M18.7474 17.8957V16.1914C18.7468 15.4361 18.4954 14.7025 18.0327 14.1056C17.57 13.5087 16.9222 13.0824 16.1909 12.8936"
                            stroke="#7E57C2"
                            stroke-width="1.70429"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M13.6343 2.66797C14.3675 2.8557 15.0173 3.28211 15.4814 3.87999C15.9455 4.47786 16.1974 5.21319 16.1974 5.97004C16.1974 6.72689 15.9455 7.46222 15.4814 8.06009C15.0173 8.65796 14.3675 9.08438 13.6343 9.27211"
                            stroke="#7E57C2"
                            stroke-width="1.70429"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                      <h3 className="text-sm sm:text-base md:text-lg items-center font-bold font-poppins text-[#101828] line-clamp-1">
                        {team.teamName}
                      </h3>
                    </div>

                    <div
                      ref={menuRef}
                      className="relative flex-shrink-0 ml-2"
                      data-team-menu
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId((prev) =>
                            prev === team.id ? null : team.id
                          );
                        }}
                        className="rounded-[0.25rem] p-1 text-[#99A1AF] hover:bg-gray-100 hover:text-gray-600"
                      >
                        {/* <MoreVertical size={18} /> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="1.02rem"
                          height="1.02rem"
                          viewBox="0 0 17 17"
                          fill="none"
                        >
                          <path
                            d="M8.1811 8.86221C8.55781 8.86221 8.86319 8.55683 8.86319 8.18013C8.86319 7.80342 8.55781 7.49805 8.1811 7.49805C7.8044 7.49805 7.49902 7.80342 7.49902 8.18013C7.49902 8.55683 7.8044 8.86221 8.1811 8.86221Z"
                            stroke="#99A1AF"
                            stroke-width="1.36344"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M8.1811 4.09072C8.55781 4.09072 8.86319 3.78535 8.86319 3.40864C8.86319 3.03194 8.55781 2.72656 8.1811 2.72656C7.8044 2.72656 7.49902 3.03194 7.49902 3.40864C7.49902 3.78535 7.8044 4.09072 8.1811 4.09072Z"
                            stroke="#99A1AF"
                            stroke-width="1.36344"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M8.1811 13.6347C8.55781 13.6347 8.86319 13.3293 8.86319 12.9526C8.86319 12.5759 8.55781 12.2705 8.1811 12.2705C7.8044 12.2705 7.49902 12.5759 7.49902 12.9526C7.49902 13.3293 7.8044 13.6347 8.1811 13.6347Z"
                            stroke="#99A1AF"
                            stroke-width="1.36344"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>

                      {openMenuId === team.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="absolute right-3 top-8 z-50 w-[12rem] h-[8.3rem] rounded-[10px] border border-[#E5E7EB] bg-white shadow-lg"
                        >
                          <div className="pt-1 text-sm items-center text-[#364153]">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowEditTeamModal(team);
                              }}
                              className="cursor-pointer w-full  inline-flex py-1.5 pb-2.5 pl-4 items-center  gap-3 text-[#364153] font-poppins text-base font-normal   hover:bg-gray-100"
                            >
                              <span className="flex items-center justify-between">
                                <Edit size={16} />
                              </span>
                              Edit Team
                            </div>
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowAddUserModal(team);
                              }}
                              className="cursor-pointer w-full inline-flex py-1.5 pb-2.5  pl-4 items-center  gap-3 text-[#364153] font-poppins text-base font-normal  hover:bg-gray-100"
                            >
                              <span className="flex items-center justify-between">
                                <UserPlus size={16} />
                              </span>
                              Add Members
                            </div>
                            <div className="h-[0.0625rem] mb-0.5 border border-[#E5E7EB]"></div>
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTeam(team);
                              }}
                              className="cursor-pointer w-full inline-flex items-center py-1.5 pb-2.5 pl-4   gap-3 text-[#E7000B] font-poppins text-base font-normal   hover:bg-gray-100"
                            >
                              <span className="flex items-center justify-between">
                                <Trash2 size={14} />
                              </span>
                              Delete Team
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-3 sm:mb-4">
                    <div className="flex justify-between items-center gap-2 text-xs sm:text-sm md:text-base font-normal font-poppins text-[#6A7282]">
                      <span className="min-w-0 flex-shrink  text-[#6A7282]">
                        Program Manager
                      </span>
                      <span className="text-[#101828] font-normal text-right truncate flex-shrink-0 max-w-[60%]">
                        {programManager?.firstName} {programManager?.lastName}
                      </span>
                    </div>

                    <div className="flex justify-between gap-2 text-xs sm:text-sm md:text-base font-normal font-poppins text-[#6A7282]">
                      <span className="min-w-0 flex-shrink">Region</span>
                      <span className="text-[#101828] font-normal text-right truncate flex-shrink-0 max-w-[60%]">
                        {team.regionName}
                      </span>
                    </div>

                    <div className="flex justify-between gap-2 text-xs sm:text-sm md:text-base font-normal font-poppins text-[#6A7282]">
                      <span className="min-w-0 flex-shrink">Total Members</span>
                      <span className="font-medium font-poppins text-[#7E57C2] flex-shrink-0">
                        {team.members.length}
                      </span>
                    </div>
                  </div>

                  <div className="h-[0.06rem] border border-[#C9C9C9] mb-[1.28rem] "></div>

                  <div>
                    <div className="flex items-center gap-2 sm:gap-3 mb-[0.6rem] sm:mb-3">
                      <h4 className="m-0 text-[0.875rem] font-normal text-[#7e57c2] leading-[1]">
                        Role Distribution
                      </h4>

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width=".25rem"
                        height=".5rem"
                        viewBox="0 0 6 10"
                        fill="none"
                        className="flex-shrink-0 self-center"
                      >
                        <path
                          d="M0.681641 8.86225L4.77195 4.77195L0.681641 0.681641"
                          stroke="#7E57C2"
                          strokeWidth="1.36344"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {Object.entries(roleCounts).filter(([role, count]) => (!NOT_ALLOWED_ROLES?.includes(role))).map(([role, count]) => (
                        <span
                          key={role}
                          className="inline-flex items-center gap-2 rounded px-2 py-[0.125rem]  "
                          style={{
                            border: "1px solid #C9C9C9",
                            backgroundColor: "white",
                            borderRadius: "0.375rem",
                          }}
                        >
                          <span
                            style={{
                              color: "#747474",
                              fontFamily: "Poppins, sans-serif",
                              fontWeight: "normal",
                              fontSize: "1rem",
                              lineHeight: "1.02256rem",
                            }}
                          >
                            {role}
                          </span>
                          <span
                            className="inline-flex items-center justify-center rounded"
                            style={{
                              // border: "1px solid #C9C9C9",
                              backgroundColor: "white",
                              color: "#747474",
                              fontFamily: "Poppins, sans-serif",
                              // fontWeight: "500",
                              fontSize: "1rem",
                              borderRadius: "0.25rem",
                              padding: "0.125rem 0.375rem",
                              minWidth: "fit-content",
                            }}
                          >
                            {count}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {addTeameModal && (
        <AddTeamModal
          handleCloseAddTeamModal={handleCloseAddTeamModal}
          showAddTeameModal={addTeameModal}
          RefreshListOfTeam={GetAllTeams}
          editTeam={editTeamData}
        />
      )}

      <AddUserModal
        isOpen={addUserModal}
        onClose={handleCloseAddUserModal}
        onSuccess={handleUserAddSuccess}
        initialTeam={teamForAddUser}
      />
      <EditUserModal
        isOpen={editUserModal}
        onClose={handleCloseEditUserModal}
        userId={selectedUserId}
        onSuccess={handleUserAddSuccess}
      />
    </>
  );
};

export default TeamsTab;
